import { ObjectUtils } from '@mm/utils';
import _ from 'lodash';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import ButtonController from '../controllers/ButtonController';
import commonHandler from '../errorHandlers/common-handler';
import WidgetModelHelpers from '../helpers/widget-model-helpers';
import ButtonModel, { ButtonRequestModel } from '../model/ButtonModel';
import { StoreState } from '../store-interfaces';
import ButtonChangeManager from '../syncEngine/services/changeManagers/ButtonChangeManager';
import EntryButtonChangeManager from '../syncEngine/services/changeManagers/EntryButtonChangeManager';
import CrudActionContainer from './CrudActionContainer';
import pageCRUDActions from './PageCRUDActions';
import WidgetCRUDActions from './WidgetCRUDActions';

type ButtonBundle = Partial<ButtonModel> & {
  bundleType: 'path';
  createInsteadId?: number;
  pageGroupId?: number;
  newDialogueName?: string;
};

class ButtonCRUDActions extends CrudActionContainer {
  retrievedEntryAction: string;
  createEntryAction: string;
  updateEntryAction: string;
  deleteEntryAction: string;

  constructor() {
    super('BUTTON');
    this.retrievedEntryAction = this.makeActionKey('RETRIEVED_ENTRY');
    this.createEntryAction = this.makeActionKey('CREATE_ENTRY');
    this.updateEntryAction = this.makeActionKey('UPDATE_ENTRY');
    this.deleteEntryAction = this.makeActionKey('DELETE_ENTRY');
  }

  create(widgetId: number, button: Partial<ButtonModel>, skipBusy = false) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!skipBusy) {
        dispatch(this.busy(this.createAction));
      }

      try {
        const response = await ButtonController.postButton(widgetId, button);
        dispatch(this.retrievedButton(widgetId, response));
        dispatch(WidgetCRUDActions.show(widgetId));

        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  update(
    widgetId: number,
    button: ButtonModel,
    withPatch = true,
    skipBusy = false,
  ) {
    return (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ): Promise<ButtonModel> => {
      const {
        widget: { entities: widgets },
      } = getState();
      const widgetModel = widgets.find(widget => widget.id === widgetId);

      if (!widgetModel) {
        return Promise.reject('widgetModel does not exist');
      }

      const prevButtonValue = WidgetModelHelpers.getButton(
        widgetModel,
        button.id,
      );

      if (!skipBusy) {
        dispatch(this.busy(this.updateAction));
      }
      const buttonWithWidgetId = { ..._.cloneDeep(button), widgetId };

      ObjectUtils.removeProperty('file', button);
      WidgetModelHelpers.removeButtonPageHiddenIfInvalid(button);

      dispatch(this.updateLocal(buttonWithWidgetId));

      let promise = ButtonChangeManager.update(
        prevButtonValue,
        button,
        widgetId,
      );

      if (withPatch) {
        promise = promise.then(response => {
          dispatch(this.updateLocal({ ...response, widgetId }));
          dispatch(WidgetCRUDActions.show(widgetId));

          return response;
        });
      }

      return promise.catch(commonHandler(dispatch));
    };
  }

  delete(
    widgetId: number,
    button: ButtonModel,
    withPatch = true,
    skipBusy = false,
  ) {
    return (dispatch: Dispatch<StoreState>): Promise<ButtonModel> => {
      if (!skipBusy) {
        dispatch(this.busy(this.deleteAction));
      }

      const buttonWithWidgetId = { ...button, widgetId };
      dispatch(this.deleteLocal(buttonWithWidgetId));

      let promise = ButtonChangeManager.delete(button, widgetId);

      if (withPatch) {
        promise = promise.then(response => {
          dispatch(WidgetCRUDActions.show(widgetId));
          return response;
        });
      }

      return promise.catch(commonHandler(dispatch));
    };
  }

  createEntry(widgetId: number, entryId: number, button: ButtonRequestModel) {
    return (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      return ButtonController.postEntry(widgetId, entryId, button).then(
        response => {
          dispatch(
            this.retrievedEntry(widgetId, entryId, button as ButtonModel),
          );
          dispatch(WidgetCRUDActions.show(widgetId));

          return response;
        },
      );
    };
  }

  updateEntry(
    widgetId: number,
    entryId: number,
    button: ButtonModel,
    withPatch = true,
  ) {
    return (dispatch, getState) => {
      const {
        widget: { entities: widgets },
      } = getState();
      const widgetModel = widgets.find(widget => widget.id === widgetId);
      const prevButton = WidgetModelHelpers.getEntryButton(
        widgetModel,
        entryId,
        button.id,
      );
      dispatch(this.busy(this.updateEntryAction));
      const buttonWithIds = { ...button, widgetId, entryId };
      dispatch(this.getUpdateEntryAction(buttonWithIds));

      ObjectUtils.removeProperty('file', button);
      let promise = EntryButtonChangeManager.update(
        prevButton,
        button,
        widgetId,
        entryId,
      );

      if (withPatch) {
        promise = promise.then(response => {
          dispatch(this.updateLocal({ ...response, widgetId, entryId }));
          dispatch(WidgetCRUDActions.show(widgetId));

          return response;
        });
      }

      return promise.catch(commonHandler(dispatch));
    };
  }

  deleteEntry(
    widgetId: number,
    entryId: number,
    button: ButtonModel,
    withPatch = true,
  ) {
    return (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteEntryAction));

      const buttonsWithIds = { ...button, widgetId, entryId };
      dispatch(this.getDeleteEntryAction(buttonsWithIds));

      let promise = EntryButtonChangeManager.delete(button, widgetId, entryId);

      if (withPatch) {
        promise = promise.then(response => {
          dispatch(WidgetCRUDActions.show(widgetId));

          return response;
        });
      }

      return promise.catch(commonHandler(dispatch));
    };
  }

  createButtonBundle(
    widgetId: number,
    entity: ButtonBundle,
    chatBotId: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      try {
        const response: ButtonModel = await ButtonController.createButtonBundle(
          widgetId,
          entity,
        );

        dispatch(this.retrievedButton(widgetId, response));
        dispatch(pageCRUDActions.retrieveAll(chatBotId));
        dispatch(WidgetCRUDActions.show(widgetId));

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  deleteButtonBundle(widgetId: number, buttonId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      try {
        const response = await ButtonController.deleteButtonBundle(
          widgetId,
          buttonId,
        );
        dispatch(WidgetCRUDActions.show(widgetId));

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  getUpdateEntryAction = entity => ({ type: this.updateEntryAction, entity });

  getDeleteEntryAction = entity => ({ type: this.deleteEntryAction, entity });

  retrievedButton = (widgetId: number, button: ButtonModel) => ({
    type: this.retrievedAction,
    payload: { button, widgetId },
  });

  retrievedEntry = (
    widgetId: number,
    entryId: number,
    button: ButtonModel,
  ) => ({
    type: this.retrievedEntryAction,
    payload: {
      widgetId,
      entryId,
      button,
    },
  });
}

const buttonCRUDActions = new ButtonCRUDActions();

export default buttonCRUDActions;

export const useButtonCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      create: (...args: Parameters<ButtonCRUDActions['create']>) =>
        dispatch(buttonCRUDActions.create(...args)),
      update: (...args: Parameters<ButtonCRUDActions['update']>) =>
        dispatch(buttonCRUDActions.update(...args)),
      delete: (...args: Parameters<ButtonCRUDActions['delete']>) =>
        dispatch(buttonCRUDActions.delete(...args)),
      createEntry: (...args: Parameters<ButtonCRUDActions['createEntry']>) =>
        dispatch(buttonCRUDActions.createEntry(...args)),
      updateEntry: (...args: Parameters<ButtonCRUDActions['updateEntry']>) =>
        dispatch(buttonCRUDActions.updateEntry(...args)),
      deleteEntry: (...args: Parameters<ButtonCRUDActions['deleteEntry']>) =>
        dispatch(buttonCRUDActions.deleteEntry(...args)),
    }),
    [dispatch],
  );
};
